/* ============================================================================
 * bundle-picker.js
 *
 * Native replacement for the MBC Bundles modal flow on the product page.
 * Wires the Bundle & Save tier selector, opens a self-contained modal with
 * N rows for picking colour + size, and submits all line items to
 * /cart/add.js in a single request. Discounting is handled by Shopify
 * automatic discounts at the cart, not here — we just collect variants.
 * ========================================================================== */
(function () {
  'use strict';

  // Bundle tier → discount % (display only; Shopify applies the real discount).
  const TIER_DISCOUNT = { 1: 0, 3: 10, 5: 15, 10: 20 };

  function optionIndex(product, candidates) {
    if (!product || !Array.isArray(product.options)) return -1;
    for (let i = 0; i < product.options.length; i++) {
      const name = String(product.options[i] || '').toLowerCase().trim();
      if (candidates.indexOf(name) !== -1) return i;
    }
    return -1;
  }

  // Read a currently-checked radio value from the main product form by its
  // `name` (matches markup at main-product.liquid lines ~1097 and ~1218 where
  // each option renders a radio group named exactly after the option, e.g.
  // "Color"/"Colour"/"Size"). Falls back to a case-insensitive sweep so we
  // don't care whether the merchant spells it "Color" or "Colour".
  function readMainProductOption(optionName) {
    if (!optionName) return null;
    const exact = document.querySelector(
      'input[type="radio"][name="' + optionName + '"]:checked'
    );
    if (exact && exact.value) return exact.value;
    const all = document.querySelectorAll('input[type="radio"]:checked');
    for (let i = 0; i < all.length; i++) {
      const input = all[i];
      if (
        input.name &&
        input.name.toLowerCase() === optionName.toLowerCase() &&
        input.value
      ) {
        return input.value;
      }
    }
    return null;
  }

  /* ---------- Tier selector wiring -------------------------------------- */

  function syncCurrentTierFromActive(ctx) {
    const active = ctx.wrapper.querySelector('.bundle-selection .active-label');
    if (!active) {
      ctx.currentTier = 1;
      return;
    }
    const label = active.closest('label');
    if (!label) return;
    if (label.hasAttribute('data-reset')) {
      ctx.currentTier = 1;
    } else {
      const n = parseInt(label.getAttribute('data-packamount'), 10);
      ctx.currentTier = isNaN(n) ? 1 : n;
    }
  }

  function wireTierSelector(ctx) {
    ctx.tierLabels.forEach(function (label) {
      label.addEventListener('click', function () {
        // Update visual active state — match prior product-bundle.js behaviour.
        ctx.wrapper.querySelectorAll('.active-label').forEach(function (el) {
          el.classList.remove('active-label');
        });
        const dot = label.querySelector('.custom-radio-dot');
        if (dot) dot.classList.add('active-label');

        // Update tier state.
        if (label.hasAttribute('data-reset')) {
          ctx.currentTier = 1;
        } else {
          const n = parseInt(label.getAttribute('data-packamount'), 10);
          ctx.currentTier = isNaN(n) ? 1 : n;
        }

        updateCtaText(ctx, label);
        updateAtcVisibility(ctx);
      });
    });
  }

  // When a multi-pack tier is active, the bundle CTA is the entry point and the
  // regular product-form ATC is hidden. When Single is active, the regular ATC
  // takes over (no modal, no bundle properties).
  function updateAtcVisibility(ctx) {
    const fakeBtns = document.querySelector('.fake-form-btns');
    const formBtns = document.querySelector('.product-form__buttons');
    const showBundle = ctx.currentTier > 1;
    if (fakeBtns) fakeBtns.style.display = showBundle ? 'block' : 'none';
    if (formBtns) formBtns.style.display = showBundle ? 'none' : 'block';
  }

  function updateCtaText(ctx, activeLabel) {
    if (!ctx.ctaBtn) return;
    const n = ctx.currentTier;
    const priceEl = activeLabel.querySelector('.pack-item-price-discounted');
    const priceText = priceEl ? priceEl.textContent.trim() : '';
    const noun = n === 1 ? 'item' : 'items';
    ctx.ctaBtn.innerHTML = 'ADD ' + n + ' ' + noun.toUpperCase() + ' TO CART &nbsp; ' + priceText;
  }

  /* ---------- Modal open / close ---------------------------------------- */

  function wireCta(ctx) {
    if (!ctx.ctaBtn) return;
    ctx.ctaBtn.addEventListener('click', function () {
      openModal(ctx, ctx.currentTier);
    });
    // Allow keyboard activation since the CTA is a div.
    ctx.ctaBtn.setAttribute('role', 'button');
    ctx.ctaBtn.setAttribute('tabindex', '0');
    ctx.ctaBtn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        openModal(ctx, ctx.currentTier);
      }
    });
  }

  function wireModalDismiss(ctx) {
    ctx.modal.querySelectorAll('[data-bundle-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeModal(ctx);
      });
    });
  }

  function openModal(ctx, n) {
    if (!ctx.product || !Array.isArray(ctx.product.variants) || ctx.product.variants.length === 0) {
      console.warn('[bundle-picker] product has no variants');
      return;
    }
    ctx.lastTrigger = document.activeElement;

    // Reset state
    hideError(ctx);
    ctx.rows = [];
    ctx.rowsContainer.innerHTML = '';
    ctx.countEls.forEach(function (el) { el.textContent = String(n); });
    if (ctx.packnumEl) ctx.packnumEl.textContent = String(n);
    setSubmitLabel(ctx, n);

    // Sync from the main product form: pre-fill colour AND size for each row
    // so the modal opens with a fully-resolved variant. The customer can
    // change either per row before adding to cart.
    const mainSize = ctx.sizeIndex !== -1
      ? readMainProductOption(ctx.product.options[ctx.sizeIndex])
      : null;
    const mainColor = ctx.colorIndex !== -1
      ? readMainProductOption(ctx.product.options[ctx.colorIndex])
      : null;

    for (let i = 0; i < n; i++) {
      const row = createRow(ctx, i);
      ctx.rowsContainer.appendChild(row.el);
      ctx.rows.push(row);
      preselectRow(ctx, row, mainColor, mainSize);
    }
    refreshTotals(ctx);

    // Show
    ctx.modal.classList.remove('-closed');
    ctx.modal.removeAttribute('hidden');
    document.body.classList.add('stop-scroll');

    // Focus trap (uses theme's global utilities)
    if (typeof window.trapFocus === 'function') {
      const firstFocusable = ctx.modalContent.querySelector(
        'button, [href], select, input, [tabindex]:not([tabindex="-1"])'
      ) || ctx.modalContent;
      window.trapFocus(ctx.modalContent, firstFocusable);
    } else {
      ctx.modalContent.focus();
    }

    // Esc to close
    ctx.escHandler = function (e) {
      if (e.key === 'Escape' || e.code === 'Escape') {
        e.stopPropagation();
        closeModal(ctx);
      }
    };
    document.addEventListener('keydown', ctx.escHandler);
  }

  function closeModal(ctx) {
    ctx.modal.classList.add('-closed');
    ctx.modal.setAttribute('hidden', '');
    document.body.classList.remove('stop-scroll');
    if (ctx.escHandler) {
      document.removeEventListener('keydown', ctx.escHandler);
      ctx.escHandler = null;
    }
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(ctx.lastTrigger || null);
    } else if (ctx.lastTrigger && typeof ctx.lastTrigger.focus === 'function') {
      ctx.lastTrigger.focus();
    }
  }

  /* ---------- Row rendering --------------------------------------------- */

  function createRow(ctx, index) {
    const product = ctx.product;
    const rowEl = document.createElement('div');
    rowEl.className = 'bundle-row';
    rowEl.setAttribute('data-row-index', String(index));

    // ---- thumbnail (defaults to product's featured image)
    const thumbEl = document.createElement('div');
    thumbEl.className = 'bundle-row__thumb';
    rowEl.appendChild(thumbEl);

    // ---- controls
    const controls = document.createElement('div');
    controls.className = 'bundle-row__controls';

    // Colour swatches (only if product has a colour option). Mirrors the main
    // product's exact DOM structure so the existing `.product-form__input
    // input[type=radio]+label` CSS applies — identical styling for free.
    let swatchesEl = null;
    if (ctx.colorIndex !== -1) {
      const groupName = 'bundle-color-' + index + '-' + Math.random().toString(36).slice(2, 8);

      const colorHeader = document.createElement('div');
      colorHeader.className = 'color-swatches-header';
      const colorLegend = document.createElement('span');
      colorLegend.className = 'form__label';
      colorLegend.textContent = product.options[ctx.colorIndex] + ':';
      colorHeader.appendChild(colorLegend);
      controls.appendChild(colorHeader);

      swatchesEl = document.createElement('div');
      swatchesEl.className = 'bundle-row__swatches product-form__input color-swatches';
      swatchesEl.setAttribute('role', 'radiogroup');
      swatchesEl.setAttribute(
        'aria-label',
        product.options[ctx.colorIndex] + ' for item ' + (index + 1)
      );

      const colorValues = uniqueOptionValues(product, ctx.colorIndex);
      colorValues.forEach(function (val, vIdx) {
        const swatchInfo = ctx.colorSwatches[val] || {};
        const valueHandle = swatchInfo.handle || val.toLowerCase().replace(/\s+/g, '-');
        const inputId = groupName + '-' + vIdx;

        const wrap = document.createElement('div');
        wrap.className = 'filter__checkbox filter__checkbox--color';

        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = groupName;
        input.value = val;
        input.setAttribute('data-color', val);
        input.addEventListener('change', function () {
          if (input.checked) selectColor(ctx, row, val);
        });
        wrap.appendChild(input);

        const lbl = document.createElement('label');
        lbl.setAttribute('for', inputId);
        lbl.setAttribute('data-swatch-color', valueHandle);
        lbl.setAttribute('aria-label', val);
        lbl.title = val;
        if (swatchInfo.image) lbl.style.backgroundImage = 'url(' + swatchInfo.image + ')';
        if (swatchInfo.hex) lbl.style.backgroundColor = swatchInfo.hex;
        // Match the main product's special-cased white/light swatch class so
        // it gets a visible border (otherwise white-on-white disappears).
        if (
          valueHandle === 'white' ||
          valueHandle === 'snow-cone' ||
          valueHandle === 'marshmallow'
        ) {
          lbl.classList.add('white-color');
        }
        wrap.appendChild(lbl);

        const tip = document.createElement('div');
        tip.className = 'tooltip';
        tip.textContent = val;
        wrap.appendChild(tip);

        swatchesEl.appendChild(wrap);
      });
      controls.appendChild(swatchesEl);
    }

    // Size dropdown (only if product has a size option)
    let sizeSelect = null;
    if (ctx.sizeIndex !== -1) {
      const sizeWrap = document.createElement('div');
      sizeWrap.className = 'bundle-row__size-wrap';

      const sizeLabel = document.createElement('label');
      sizeLabel.className = 'bundle-row__group-label';
      sizeLabel.setAttribute('for', 'bundle-size-' + index);
      sizeLabel.textContent = product.options[ctx.sizeIndex];
      controls.appendChild(sizeLabel);

      sizeSelect = document.createElement('select');
      sizeSelect.id = 'bundle-size-' + index;
      sizeSelect.className = 'bundle-row__size';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select a size';
      sizeSelect.appendChild(placeholder);
      sizeSelect.addEventListener('change', function () {
        selectSize(ctx, row, sizeSelect.value);
      });
      sizeWrap.appendChild(sizeSelect);
      controls.appendChild(sizeWrap);
    }

    rowEl.appendChild(controls);

    // ---- price column
    const priceEl = document.createElement('div');
    priceEl.className = 'bundle-row__price';
    const priceEmpty = document.createElement('span');
    priceEmpty.className = 'bundle-row__price-empty';
    priceEmpty.textContent = '—';
    priceEl.appendChild(priceEmpty);
    rowEl.appendChild(priceEl);

    const row = {
      index: index,
      el: rowEl,
      thumbEl: thumbEl,
      swatchesEl: swatchesEl,
      sizeSelect: sizeSelect,
      priceEl: priceEl,
      color: null,
      size: null,
      variant: null,
      previewVariant: null,
    };

    // Default thumbnail: product's featured image.
    renderRowDefaultThumb(ctx, row);

    // Populate size options now (dropdown is independent of colour selection
    // — it filters available stock once a colour is picked).
    if (sizeSelect) populateSizeOptions(ctx, row);

    // If the product has neither colour nor size, just pick the first available variant.
    if (ctx.colorIndex === -1 && ctx.sizeIndex === -1) {
      const v = product.variants.find(function (vv) { return vv.available; }) || product.variants[0];
      row.variant = v;
      renderRowPrice(ctx, row, v);
      renderRowThumb(row, v);
    } else {
      // Show a sample price upfront — picked variants will replace it as the
      // user makes selections.
      row.previewVariant = findRepresentativeVariant(ctx, row);
      if (row.previewVariant) renderRowPrice(ctx, row, row.previewVariant);
    }

    return row;
  }

  function uniqueOptionValues(product, optionIndex) {
    const seen = new Set();
    const out = [];
    product.variants.forEach(function (v) {
      const val = v['option' + (optionIndex + 1)];
      if (val && !seen.has(val)) {
        seen.add(val);
        out.push(val);
      }
    });
    return out;
  }

  // Apply default colour/size to a freshly-created row. Colour is set first so
  // the size dropdown's stock filter is correct before we try to honour size.
  function preselectRow(ctx, row, color, size) {
    if (color && row.swatchesEl) {
      const input = row.swatchesEl.querySelector(
        'input[type="radio"][data-color="' + cssEscape(color) + '"]'
      );
      if (input) selectColor(ctx, row, color);
    }
    if (size && row.sizeSelect) {
      const opt = row.sizeSelect.querySelector(
        'option[value="' + cssEscape(size) + '"]'
      );
      if (opt && !opt.disabled) {
        row.sizeSelect.value = size;
        row.size = size;
        resolveVariant(ctx, row);
      }
    }
  }

  function selectColor(ctx, row, color) {
    row.color = color;
    if (row.swatchesEl) {
      row.swatchesEl.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = input.getAttribute('data-color') === color;
      });
    }
    if (row.sizeSelect) {
      // Refresh size availability for the new colour. Preserve the user's size
      // choice if it's still available; otherwise reset.
      const previousSize = row.size;
      populateSizeOptions(ctx, row);
      if (previousSize) {
        const opt = row.sizeSelect.querySelector('option[value="' + cssEscape(previousSize) + '"]');
        if (opt && !opt.disabled) {
          row.sizeSelect.value = previousSize;
        } else {
          row.sizeSelect.value = '';
          row.size = null;
        }
      }
    }
    resolveVariant(ctx, row);
  }

  function selectSize(ctx, row, size) {
    row.size = size || null;
    resolveVariant(ctx, row);
  }

  function populateSizeOptions(ctx, row) {
    const select = row.sizeSelect;
    if (!select) return;
    const product = ctx.product;
    const sizeIdx = ctx.sizeIndex;
    const colorIdx = ctx.colorIndex;

    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select size';
    select.appendChild(placeholder);

    const sizeValues = uniqueOptionValues(product, sizeIdx);
    sizeValues.forEach(function (size) {
      const matchingVariants = product.variants.filter(function (v) {
        if (colorIdx !== -1 && row.color && v['option' + (colorIdx + 1)] !== row.color) return false;
        return v['option' + (sizeIdx + 1)] === size;
      });
      // If the product has additional options we don't know about, treat as available
      // if any matching variant is available.
      const anyAvailable = matchingVariants.some(function (v) { return v.available; });
      const exists = matchingVariants.length > 0;

      const opt = document.createElement('option');
      opt.value = size;
      if (!exists) {
        opt.disabled = true;
        opt.textContent = size + ' — Unavailable';
      } else if (!anyAvailable) {
        opt.disabled = true;
        opt.textContent = size + ' — Out of stock';
      } else {
        opt.textContent = size;
      }
      select.appendChild(opt);
    });
  }

  function resolveVariant(ctx, row) {
    const product = ctx.product;
    const variant = product.variants.find(function (v) {
      if (ctx.colorIndex !== -1) {
        if (v['option' + (ctx.colorIndex + 1)] !== row.color) return false;
      }
      if (ctx.sizeIndex !== -1) {
        if (v['option' + (ctx.sizeIndex + 1)] !== row.size) return false;
      }
      return true;
    }) || null;

    row.variant = variant && variant.available ? variant : null;
    if (row.variant) {
      row.previewVariant = null;
      renderRowPrice(ctx, row, row.variant);
      renderRowThumb(row, row.variant);
    } else {
      // Selection is not yet complete or not available.
      const isComplete =
        (ctx.colorIndex === -1 || row.color) && (ctx.sizeIndex === -1 || row.size);
      if (variant && !variant.available && isComplete) {
        row.previewVariant = null;
        renderRowPriceUnavailable(row);
      } else {
        // Partial selection — show a sample price using a representative variant
        // of the chosen colour (most variants share a price). Falls back to the
        // first available variant of the product when no colour is picked yet.
        row.previewVariant = findRepresentativeVariant(ctx, row);
        if (row.previewVariant) {
          renderRowPrice(ctx, row, row.previewVariant);
        } else {
          renderRowPriceEmpty(row);
        }
      }
      // Show a colour-specific image once a colour is picked, even before size.
      if (row.color) {
        const colourPreview = product.variants.find(function (v) {
          return v['option' + (ctx.colorIndex + 1)] === row.color &&
            v.featured_image && (v.featured_image.src || typeof v.featured_image === 'string');
        });
        if (colourPreview) {
          renderRowThumb(row, colourPreview);
        } else {
          renderRowDefaultThumb(ctx, row);
        }
      } else {
        renderRowDefaultThumb(ctx, row);
      }
    }
    refreshTotals(ctx);
  }

  // Find a stand-in variant for a row whose selection isn't complete yet.
  // Prefers an available variant matching the chosen colour, then any variant
  // of that colour, then any available variant of the product.
  function findRepresentativeVariant(ctx, row) {
    const variants = ctx.product.variants;
    if (!variants || !variants.length) return null;
    const colorIdx = ctx.colorIndex;
    const matchesColor = function (v) {
      if (colorIdx === -1 || !row.color) return true;
      return v['option' + (colorIdx + 1)] === row.color;
    };
    return (
      variants.find(function (v) { return matchesColor(v) && v.available; }) ||
      variants.find(matchesColor) ||
      variants.find(function (v) { return v.available; }) ||
      variants[0] ||
      null
    );
  }

  function renderRowDefaultThumb(ctx, row) {
    row.thumbEl.innerHTML = '';
    const product = ctx.product;
    const src = (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      (product.images && product.images[0]) ||
      null;
    if (src) {
      const img = document.createElement('img');
      img.src = typeof src === 'string' ? src : (src.src || '');
      img.alt = '';
      img.loading = 'lazy';
      row.thumbEl.appendChild(img);
    }
  }

  function renderRowPrice(ctx, row, variant) {
    const tier = ctx.currentTier;
    const pct = TIER_DISCOUNT[tier] || 0;
    const fullCents = Number(variant.price);
    const discountedCents = pct > 0
      ? Math.round((fullCents * (100 - pct)) / 100)
      : fullCents;

    row.priceEl.innerHTML = '';
    if (pct > 0) {
      const compare = document.createElement('span');
      compare.className = 'bundle-row__price-compare';
      compare.textContent = formatMoney(fullCents);
      row.priceEl.appendChild(compare);
    }
    const current = document.createElement('span');
    current.className = 'bundle-row__price-current';
    current.textContent = formatMoney(discountedCents);
    row.priceEl.appendChild(current);
  }

  function renderRowPriceEmpty(row) {
    row.priceEl.innerHTML = '<span class="bundle-row__price-empty">—</span>';
  }

  function renderRowPriceUnavailable(row) {
    row.priceEl.innerHTML = '<span class="bundle-row__price-empty">Unavailable</span>';
  }

  function renderRowThumb(row, variant) {
    row.thumbEl.innerHTML = '';
    const src =
      (variant.featured_image && (variant.featured_image.src || variant.featured_image)) ||
      null;
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      row.thumbEl.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'bundle-row__thumb-placeholder';
      placeholder.textContent = '';
      row.thumbEl.appendChild(placeholder);
    }
  }

  /* ---------- Totals & submit gate -------------------------------------- */

  function refreshTotals(ctx) {
    const completeRows = ctx.rows.filter(function (r) { return r.variant; });
    // Include preview variants so the total reflects an estimate even before
    // every row is fully picked. Submit stays gated on `completeRows` below.
    const pricedRows = ctx.rows.filter(function (r) {
      return r.variant || r.previewVariant;
    });
    const fullCents = pricedRows.reduce(function (acc, r) {
      const v = r.variant || r.previewVariant;
      return acc + Number(v.price);
    }, 0);

    const tier = ctx.currentTier;
    const pct = TIER_DISCOUNT[tier] || 0;
    const discountedCents = pct > 0
      ? Math.round((fullCents * (100 - pct)) / 100)
      : fullCents;

    if (pct > 0 && fullCents > 0) {
      ctx.compareEl.textContent = formatMoney(fullCents);
      ctx.compareEl.removeAttribute('hidden');
    } else {
      ctx.compareEl.textContent = '';
      ctx.compareEl.setAttribute('hidden', '');
    }

    if (fullCents > 0) {
      const suffix = ctx.currencyCode ? ' ' + ctx.currencyCode : '';
      ctx.currentEl.textContent = formatMoney(discountedCents) + suffix;
    } else {
      ctx.currentEl.textContent = '';
    }

    const allComplete = ctx.rows.length > 0 && completeRows.length === ctx.rows.length;
    setSubmitEnabled(ctx, allComplete);
  }

  function setSubmitEnabled(ctx, enabled) {
    if (enabled) {
      ctx.submitBtn.disabled = false;
      ctx.submitBtn.removeAttribute('aria-disabled');
    } else {
      ctx.submitBtn.disabled = true;
      ctx.submitBtn.setAttribute('aria-disabled', 'true');
    }
  }

  function setSubmitLabel(ctx /* n unused — copy stays "Add to Cart" */) {
    if (!ctx.submitLabel) return;
    ctx.submitLabel.textContent = 'Add to Cart';
  }

  /* ---------- Submit ---------------------------------------------------- */

  function tierLabelFor(n) {
    return n + 'pack'; // e.g. 3pack, 5pack, 10pack, 1pack
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function buildSubmitPayload(ctx) {
    const tier = ctx.currentTier;
    const tierTag = tierLabelFor(tier);
    const items = ctx.rows.map(function (r) {
      const item = {
        id: r.variant.id,
        quantity: 1,
      };
      if (tier > 1) {
        item.properties = { _bundle_tier: tierTag };
      }
      return item;
    });
    const payload = { items: items };

    const cart = getCartDrawer();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    return { payload: payload, cart: cart };
  }

  function submitBundle(ctx) {
    if (ctx.submitBtn.disabled) return;

    hideError(ctx);
    ctx.submitBtn.disabled = true;
    ctx.submitBtn.setAttribute('aria-disabled', 'true');
    ctx.submitBtn.classList.add('is-loading');

    const built = buildSubmitPayload(ctx);

    // Optimistic open: close picker, pop drawer with N populated
    // previews (one per bundle item) before the network call. Server
    // rendering 3–10 line items is the slowest path; this hides the
    // wait behind the existing drawer slide-in animation.
    closeModal(ctx);
    const optimisticQty = (built.payload.items || []).reduce(function (sum, it) {
      return sum + (parseInt(it.quantity, 10) || 1);
    }, 0);

    // Build preview data from the bundle rows. Each row holds the
    // resolved variant plus the bundle product context (title, options).
    // Apply TIER_DISCOUNT optimistically so the preview line shows the
    // bundle's discounted price + strikethrough original — matches how
    // Shopify will render it ~500ms later when the cart discount lands.
    //
    // Guard: if the cart already carries discounts (cart-level promo,
    // existing bundle line items, automatic discount) we can't safely
    // predict the final price — Shopify's combine rules may apply the
    // new tier differently. Skip the strikethrough and show regular
    // prices; the server-rendered line replaces this in ~500ms anyway.
    const cartHasDiscount = (function () {
      // 1) Live DOM check — most reliable, works regardless of lastCart
      //    freshness. Liquid renders <span class="cart-item__price-compare-at">
      //    strikethrough whenever a line item is discounted; existence of
      //    that span anywhere in the drawer means there's a line discount.
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.querySelector('.cart-item__price-compare-at')) return true;

      // 2) Inline state — #cart-drawer-state's total_discount field is
      //    populated from cart.total_discount on every section render,
      //    so this catches cart-level discounts even on the boot stub
      //    before the deferred /cart.js enrichment lands.
      const stateEl = document.getElementById('cart-drawer-state');
      if (stateEl) {
        try {
          const parsed = JSON.parse(stateEl.textContent);
          if (typeof parsed.total_discount === 'number' && parsed.total_discount > 0) return true;
        } catch (_) {}
      }

      // 3) lastCart — after the deferred /cart.js fetch lands we have
      //    the full cart object with line-level discount fields.
      const lc = window.cartEnhancements && window.cartEnhancements.lastCart;
      if (!lc) return false;
      if (typeof lc.total_discount === 'number' && lc.total_discount > 0) return true;
      if (Array.isArray(lc.cart_level_discount_applications) && lc.cart_level_discount_applications.length) return true;
      if (Array.isArray(lc.items)) {
        for (let i = 0; i < lc.items.length; i++) {
          const it = lc.items[i];
          if (it && Array.isArray(it.discount_allocations) && it.discount_allocations.length) return true;
          if (it && typeof it.original_price === 'number' && typeof it.final_price === 'number' && it.original_price > it.final_price) return true;
        }
      }
      return false;
    })();
    const previewItems = (function () {
      try {
        const rows = ctx.rows || [];
        const productOptions = (ctx.product && Array.isArray(ctx.product.options))
          ? ctx.product.options
          : [];
        const tier = ctx.currentTier;
        const pct = cartHasDiscount ? 0 : (TIER_DISCOUNT[tier] || 0);
        const out = [];
        rows.forEach(function (row) {
          const v = row.variant;
          if (!v) return;
          const image =
            (v.featured_image && v.featured_image.src) ||
            (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) ||
            (ctx.product && ctx.product.featured_image) ||
            null;
          // Pair variant.options with product.options to get name+value
          // pairs the preview can render as "Size: X" + a colour swatch.
          let options = null;
          if (Array.isArray(v.options) && productOptions.length) {
            options = v.options.map(function (val, i) {
              // product.options entries may be strings (older shape) or
              // objects {name, position, values}. Handle both.
              const opt = productOptions[i];
              const name = (opt && typeof opt === 'object') ? opt.name : (typeof opt === 'string' ? opt : '');
              return { name: name, value: val };
            });
          }
          const rawPrice = typeof v.price === 'number' ? v.price : null;
          // Discounted price for display. Shopify rounds line discounts
          // per cent, so round to nearest integer cent here too. If tier
          // 1 (no discount), final == regular.
          const finalPrice = (rawPrice != null && pct > 0)
            ? Math.round(rawPrice * (100 - pct) / 100)
            : rawPrice;
          const qty = parseInt(row.quantity, 10) || 1;
          for (let i = 0; i < qty; i++) {
            out.push({
              title: (ctx.product && ctx.product.title) || v.name || '',
              image: image,
              variant_title: v.title,
              options: options,
              price: finalPrice,
              // Only set when there's an actual discount — _populatePreview
              // renders strikethrough only when original > final.
              original_price: (pct > 0 && rawPrice != null) ? rawPrice : null,
            });
          }
        });
        return out.length === optimisticQty ? out : null;
      } catch (_) { return null; }
    })();

    if (window.cartEnhancements && typeof window.cartEnhancements.beginAdd === 'function' && optimisticQty > 0) {
      window.cartEnhancements.beginAdd(optimisticQty, previewItems);
    }

    fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(built.payload),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, status: response.status, body: json };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.body && result.body.status)) {
          const msg =
            (result.body && (result.body.description || result.body.message)) ||
            'Sorry, something went wrong adding your bundle. Please try again.';
          if (window.cartEnhancements && typeof window.cartEnhancements.rollbackOptimistic === 'function') {
            window.cartEnhancements.rollbackOptimistic();
          }
          showError(ctx, msg);
          setSubmitEnabled(ctx, true);
          ctx.submitBtn.classList.remove('is-loading');
          return;
        }

        // Success
        ctx.submitBtn.classList.remove('is-loading');
        const cart = built.cart;

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(result.body);
          if (typeof cart.open === 'function') {
            cart.open();
          }
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
        } else {
          // No drawer/notification — fall back to /cart navigation, matching theme.
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function (err) {
        console.error('[bundle-picker] add to cart failed', err);
        if (window.cartEnhancements && typeof window.cartEnhancements.rollbackOptimistic === 'function') {
          window.cartEnhancements.rollbackOptimistic();
        }
        showError(ctx, 'Network error — please check your connection and try again.');
        setSubmitEnabled(ctx, true);
        ctx.submitBtn.classList.remove('is-loading');
      });
  }

  /* ---------- Helpers --------------------------------------------------- */

  function showError(ctx, message) {
    ctx.errorEl.textContent = message;
    ctx.errorEl.removeAttribute('hidden');
  }
  function hideError(ctx) {
    ctx.errorEl.textContent = '';
    ctx.errorEl.setAttribute('hidden', '');
  }

  // Money formatting — cents → string. Uses Shopify.formatMoney if available
  // (theme often exposes window.Shopify with a money_format), otherwise a
  // sensible default of "$X.YY".
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const SH = window.Shopify;
    if (SH && typeof SH.formatMoney === 'function' && (SH.money_format || (window.theme && window.theme.moneyFormat))) {
      const fmt = SH.money_format || window.theme.moneyFormat;
      try {
        return SH.formatMoney(cents, fmt);
      } catch (_) { /* fall through */ }
    }
    const n = Number(cents) / 100;
    return '$' + n.toFixed(2);
  }

  // Minimal CSS.escape polyfill for older browsers.
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
      return '\\' + c.charCodeAt(0).toString(16) + ' ';
    });
  }

  /* ---------- Boot ------------------------------------------------------ */

  function bootstrap() {
    const wrapper = document.querySelector('.custom-product-bundle[data-bundle="true"]');
    if (!wrapper) return;

    const productJsonEl = document.querySelector('script[data-bundle-product-json]');
    if (!productJsonEl) return;

    let product;
    try { product = JSON.parse(productJsonEl.textContent); }
    catch (e) { console.error('[bundle-picker] product JSON parse error', e); return; }

    // Some products are split across multiple Shopify products (one per
    // colour) and stitched together via the `custom.product_color_variants`
    // metafield. Merge those variants in so the picker shows every colour.
    const linkedEl = document.querySelector('script[data-bundle-linked-variants]');
    if (linkedEl) {
      try {
        const linked = JSON.parse(linkedEl.textContent);
        if (Array.isArray(linked) && linked.length && Array.isArray(product.variants)) {
          const seen = new Set(product.variants.map(function (v) { return v.id; }));
          linked.forEach(function (v) {
            if (v && !seen.has(v.id)) {
              product.variants.push(v);
              seen.add(v.id);
            }
          });
        }
      } catch (e) {
        console.warn('[bundle-picker] linked-variants JSON parse error', e);
      }
    }

    let colorSwatches = {};
    const swatchesEl = document.querySelector('script[data-bundle-color-swatches]');
    if (swatchesEl) {
      try { colorSwatches = JSON.parse(swatchesEl.textContent) || {}; }
      catch (e) { console.warn('[bundle-picker] swatch JSON parse error', e); }
    }

    const modal = document.querySelector('.pack-modal[data-bundle-modal]');
    if (!modal) return;

    const ctx = {
      wrapper: wrapper,
      product: product,
      colorSwatches: colorSwatches,
      modal: modal,
      modalContent: modal.querySelector('.pack-modal__content'),
      rowsContainer: modal.querySelector('[data-bundle-rows]'),
      countEls: modal.querySelectorAll('[data-bundle-count]'),
      submitBtn: modal.querySelector('[data-bundle-submit]'),
      submitLabel: modal.querySelector('[data-bundle-submit-label]'),
      packnumEl: modal.querySelector('.packnum'),
      compareEl: modal.querySelector('[data-bundle-compare]'),
      currentEl: modal.querySelector('[data-bundle-current]'),
      currencyCode:
        (modal.querySelector('.pack-modal__footer[data-currency]') || {}).dataset?.currency || '',
      errorEl: modal.querySelector('[data-bundle-error]'),
      ctaBtn: document.getElementById('fake-bundle-btn-add-to-cart'),
      tierLabels: wrapper.querySelectorAll('.bundle-selection label'),
      colorIndex: optionIndex(product, ['color', 'colour']),
      sizeIndex: optionIndex(product, ['size']),
      currentTier: 1,
      rows: [],
      lastTrigger: null,
      escHandler: null,
    };

    syncCurrentTierFromActive(ctx);
    wireTierSelector(ctx);
    wireCta(ctx);
    wireModalDismiss(ctx);

    ctx.submitBtn.addEventListener('click', function () { submitBundle(ctx); });

    // Set initial CTA text from the active tier label so the price/count match
    // the visually-active tier (avoids the old hardcoded "ADD 3 Items..." state).
    const activeLabel = ctx.wrapper.querySelector(
      '.bundle-selection .active-label'
    );
    if (activeLabel) {
      const labelEl = activeLabel.closest('label');
      if (labelEl) updateCtaText(ctx, labelEl);
    }
    updateAtcVisibility(ctx);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
